import React, { useEffect, useState } from "react";
import TeamHeader from "../TeamHeader/TeamHeader";
import "./PipelineOverview.css";
import LoadingSpinner from '../../backend/components/LoadingSpinner';

// Icons
import { GoPencil } from "react-icons/go";

interface NotionAnnotations {
    bold: boolean;
    italic: boolean;
    strikethrough: boolean;
    underline: boolean;
    code: boolean;
    color: string; // e.g., "default" or any color string returned by Notion
}

interface NotionRichText {
    plain_text: string;
    href?: string | null;
    mention?: any;
    annotations: NotionAnnotations;
}


interface NotionTableRow {
    id: string;
    type: string;
    table_row: {
        cells: NotionRichText[][]; // array of cells -> each cell is an array of rich text objects
    };
}

interface NotionBlock {
    id: string;
    type: string;
    has_children?: boolean;
    table?: {
        table_width: number;
    };
    [key: string]: any;
}

const getTextStyle = (annotations: NotionAnnotations): React.CSSProperties => {
    return {
        fontWeight: annotations.bold ? "bold" : "normal",
        fontStyle: annotations.italic ? "italic" : "normal",
        textDecoration: [
            annotations.underline ? "underline" : "",
            annotations.strikethrough ? "line-through" : "",
        ].filter(Boolean).join(" "),
        fontFamily: annotations.code ? `"Fira Code", monospace` : "inherit",
        color: annotations.color !== "default" ? annotations.color : "inherit",
    };
};


const PipelineOverview = () => {
    const [blocks, setBlocks] = useState<NotionBlock[]>([]);
    const [tableRows, setTableRows] = useState<Record<string, NotionTableRow[]>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Fetching information from notion page
    useEffect(() => {
        fetch("/api/notion/page-blocks/", {
            credentials: "same-origin"
        })
            .then((res) => res.json())
            .then(async (data) => {
                const blocksData = data.blocks;
                setBlocks(blocksData);

                // fetch rows for each table block that has children
                const tableBlocks = blocksData.filter((b: NotionBlock) => b.type === "table" && b.has_children);
                const rowsMap: Record<string, NotionTableRow[]> = {};

                for (const table of tableBlocks) {
                    const res = await fetch(`/api/notion/table-rows/${table.id}/`, {
                        credentials: "same-origin"
                    });
                    const tableData = await res.json();
                    rowsMap[table.id] = tableData.rows;
                }

                setTableRows(rowsMap);
                setLoading(false);
            })
            .catch((err) => console.error("Notion fetch failed:", err));
    }, []);

    // Error in case fetch fails
    useEffect(() => {
        // --- CREATE TIMEOUT ---
        const timeout = setTimeout(() => {
            setError(true);
            setLoading(false);
        }, 10000); // 10 seconds

        fetch("/api/notion/page-blocks/", {
            credentials: "same-origin"
        })
            .then((res) => res.json())
            .then(async (data) => {
                if (error) return; // prevent overriding timeout state

                clearTimeout(timeout); // SUCCESS → cancel timeout

                const blocksData = data.blocks;
                setBlocks(blocksData);

                const tableBlocks = blocksData.filter((b: NotionBlock) => b.type === "table" && b.has_children);
                const rowsMap: Record<string, NotionTableRow[]> = {};

                for (const table of tableBlocks) {
                    const res = await fetch(`/api/notion/table-rows/${table.id}/`, {
                        credentials: "same-origin"
                    });
                    const tableData = await res.json();
                    rowsMap[table.id] = tableData.rows;
                }

                setTableRows(rowsMap);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Notion fetch failed:", err);
                clearTimeout(timeout);
                setError(true);
                setLoading(false);
            });

        return () => clearTimeout(timeout);
    }, []);


    const renderTable = (block: NotionBlock) => {
        const rows = tableRows[block.id];
        if (!rows) return <p>Loading table…</p>;

        return (
            <table key={block.id} className="notion-table">
                <thead>
                    <tr>
                        {rows[0].table_row.cells.map((cell, i) => (
                            <th key={i}>
                                {cell.map((t: NotionRichText, j: number) => (
                                    <span key={j} style={getTextStyle(t.annotations)}>
                                        {t.plain_text}
                                    </span>
                                ))}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.slice(1).map((row) => (
                        <tr key={row.id}>
                            {row.table_row.cells.map((cell, i) => (
                                <td key={i}>
                                    {cell.map((t: NotionRichText, j: number) => {
                                        const url = t.href || t.mention?.link_preview?.url;
                                        const textStyle = getTextStyle(t.annotations);

                                        if (url) {
                                            return (
                                                <a
                                                    key={j}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={textStyle}
                                                >
                                                    {t.plain_text}
                                                </a>
                                            );
                                        }

                                        return (
                                            <span key={j} style={textStyle}>
                                                {t.plain_text}
                                            </span>
                                        );
                                    })}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderBlock = (block: NotionBlock) => {
        switch (block.type) {
            case "heading_1":
                return (
                    <h1 key={block.id}>
                        {block.heading_1.rich_text.map((t: NotionRichText, i: number) => (
                            <span key={i}>{t.plain_text}</span>
                        ))}
                    </h1>
                );
            case "heading_2":
                return (
                    <h2 key={block.id}>
                        {block.heading_2.rich_text.map((t: NotionRichText, i: number) => (
                            <span key={i}>{t.plain_text}</span>
                        ))}
                    </h2>
                );
            case "heading_3":
                return (
                    <h3 key={block.id}>
                        {block.heading_3.rich_text.map((t: NotionRichText, i: number) => (
                            <span key={i}>{t.plain_text}</span>
                        ))}
                    </h3>
                );
            case "paragraph":
                return (
                    <p key={block.id}>
                        {block.paragraph.rich_text.map((t: NotionRichText, i: number) => {
                            const url = t.href || t.mention?.link_preview?.url;
                            const textStyle = getTextStyle(t.annotations);

                            if (url) {
                                return (
                                    <a
                                        key={i}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={textStyle}
                                    >
                                        {t.plain_text}
                                    </a>
                                );
                            }

                            return (
                                <span key={i} style={textStyle}>
                                    {t.plain_text}
                                </span>
                            );
                        })}
                    </p>
                );

            case "code":
                return (
                    <pre key={block.id} className="notion-code">
                        {block.code.rich_text.map((t: NotionRichText, i: number) => {
                            // Split each line by quotes to isolate keys
                            const parts = t.plain_text.split(/(".*?")/g);
                            return parts.map((part, j) => {
                                // Match JSON keys (strings followed by :)
                                if (/^".*"$/.test(part) && t.plain_text.includes(`${part}:`)) {
                                    return (
                                        <span key={j} style={{ color: "#28aee1" }}>
                                            {part}
                                        </span>
                                    );
                                }
                                return <span key={j}>{part}</span>;
                            });
                        })}
                    </pre>
                );

            case "numbered_list_item":
                return (
                    <li key={block.id}>
                        {block.numbered_list_item.rich_text.map((t: NotionRichText, i: number) => (
                            <span key={i}>{t.plain_text}</span>
                        ))}
                    </li>
                );

            case "image": {
                const img = block.image;
                const url =
                    img?.file?.url ||
                    img?.external?.url ||
                    "";

                return (
                    <div key={block.id} className="notion-image-item">
                        <img src={url} alt="" />
                    </div>
                );
            }



            case "table":
                return renderTable(block);
            default:
                return null;
        }
    };

    function renderWithImageGroups(
        blocks: NotionBlock[],
        renderBlock: (b: NotionBlock) => React.ReactNode
    ): React.ReactNode[] {

        const elements: React.ReactNode[] = [];
        let currentImageGroup: NotionBlock[] = [];

        blocks.forEach((block, index) => {
            const isImage = block.type === "image";

            if (isImage) {
                currentImageGroup.push(block);
            }

            const nextIsImage =
                index < blocks.length - 1 &&
                blocks[index + 1].type === "image";

            // When an image group ends → render it
            if (isImage && !nextIsImage) {
                elements.push(
                    <div className="notion-image-grid" key={`group-${index}`}>
                        {currentImageGroup.map(imgBlock => renderBlock(imgBlock))}
                    </div>
                );
                currentImageGroup = [];
            }

            // Render normal (non-image) blocks
            if (!isImage) {
                elements.push(renderBlock(block));
            }
        });

        return elements;
    }


    return (
        <div className="customer-directory-body" style={{ minHeight: '100vh' }}>
            <TeamHeader title="AI" />
            <div className="customer-title-and-expand-buttons" style={{ marginBottom: 'unset' }}>
                <h1 className="cutomer-title"> Pipeline Overview</h1>

                <div className="ai-expand-buttons">
                    <button
                        className="general-sort-summary"
                        onClick={() =>
                            window.open(
                                "https://www.notion.so/AI-Page-2c33634fc8d980c08a7ce4be7d222d8a",
                                "_blank",
                                "noopener,noreferrer"
                            )
                        }
                    >
                        <GoPencil style={{ marginRight: "0.25rem", fontSize: "0.9rem" }} /> Edit
                    </button>
                </div>


            </div>

            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%'
                }}
            >
                {loading ? (
                    <LoadingSpinner />
                ) : error ? (
                    <h2 style={{ fontSize: "1.25rem", color: "#cc0000" }}>
                        Failed to fetch information from Notion Page.
                    </h2>
                ) : (
                    <div className="notion-content">
                        {renderWithImageGroups(blocks, renderBlock)}
                    </div>

                )}
            </div>

        </div>
    );

};

export default PipelineOverview;
